import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Contacts as ContactsIcon,
  CalendarToday as CalendarTodayIcon,
  Cake as CakeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  LocalHospital as LocalHospitalIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import pacienteService from '../../services/pacienteService';
import PageHeader from '../../components/common/PageHeader';
import CambioEstadoPaciente from '../../components/pacientes/CambioEstadoPaciente';
import HistorialCambiosEstado from '../../components/pacientes/HistorialCambiosEstado';

// Componente de panel para las tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const getEstadoColor = (estado) => {
  switch (estado) {
    case 'Activo':
      return 'success';
    case 'Inactivo':
      return 'error';
    case 'De alta':
      return 'info';
    case 'Pausa':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = parseISO(dateString);
    const dateWithNoon = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12, 0, 0
    );
    return format(dateWithNoon, 'dd/MM/yyyy', { locale: es });
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return dateString;
  }
};

const PacienteDetail = () => {
  const { id } = useParams();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [citas, setCitas] = useState([]);
  const [citasLoading, setCitasLoading] = useState(false);
  const [cambioEstadoDialogOpen, setCambioEstadoDialogOpen] = useState(false);
  const [apoderados, setApoderados] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [documentosLoading, setDocumentosLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [documentCategory, setDocumentCategory] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  
  // Función helper para obtener el rol correctamente
  const getUserRole = () => {
    if (!user) return null;
    
    if (typeof user.rol === 'object' && user.rol !== null) {
      return user.rol.nombre || user.rol.name || user.rol.tipo || null;
    }
    
    if (typeof user.rol === 'string') {
      return user.rol;
    }
    
    return null;
  };
  
  // Verificar permisos para crear/editar
  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Verificar permisos para editar
  const canEdit = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Verificar permisos para cambiar estado
  const canChangeStatus = () => {
    const userRole = getUserRole();
    return ['Administrador'].includes(userRole);
  };
  
  // Verificar permisos para subir documentos
  const canUploadDocuments = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión', 'Especialista'].includes(userRole);
  };
  
  // Verificar permisos para eliminar documentos
  const canDeleteDocuments = () => {
    const userRole = getUserRole();
    return ['Administrador'].includes(userRole);
  };
 
  // Cargar datos del paciente
  useEffect(() => {
    const fetchPatient = async () => {
      setLoading(true);
      try {
        const response = await pacienteService.getPaciente(id);
        setPatient(response.data);
        
        // Cargar apoderados
        try {
          const apoderadosResponse = await pacienteService.getApoderados(id);
          setApoderados(apoderadosResponse.data || []);
        } catch (error) {
          console.warn('No se pudieron cargar los apoderados:', error);
          setApoderados([]);
        }
        
      } catch (error) {
        console.error('Error fetching patient:', error);
        showNotification('Error al cargar los datos del paciente', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id, showNotification]);

  // Cargar citas cuando se selecciona esa pestaña
  useEffect(() => {
    if (tabValue === 1 && citas.length === 0) {
      const fetchCitas = async () => {
        setCitasLoading(true);
        try {
          const response = await pacienteService.getCitas(id);
          setCitas(response.data);
        } catch (error) {
          console.error('Error fetching citas:', error);
          showNotification('Error al cargar las citas', 'error');
        } finally {
          setCitasLoading(false);
        }
      };

      fetchCitas();
    }
  }, [tabValue, id, citas.length, showNotification]);

  // Cargar documentos cuando se selecciona esa pestaña
  useEffect(() => {
    if (tabValue === 2) {
      const fetchDocumentos = async () => {
        setDocumentosLoading(true);
        try {
          const response = await pacienteService.getDocumentos(id);
          setDocumentos(response.data || []);
        } catch (error) {
          console.error('Error fetching documentos:', error);
          showNotification('Error al cargar los documentos', 'error');
          setDocumentos([]);
        } finally {
          setDocumentosLoading(false);
        }
      };

      fetchDocumentos();
    }
  }, [tabValue, id, showNotification]);

  const handleOpenCambioEstadoDialog = () => {
    setCambioEstadoDialogOpen(true);
  };

  const handleCloseCambioEstadoDialog = () => {
    setCambioEstadoDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEstadoChanged = (nuevoEstado) => {
    setPatient({
      ...patient,
      estado: nuevoEstado
    });
  };

  // Funciones para documentos
  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setFileToUpload(null);
    setDocumentCategory('');
    setDocumentDescription('');
  };

  const handleFileSelect = (event) => {
    setFileToUpload(event.target.files[0]);
  };

  const handleUploadDocument = async () => {
    if (!fileToUpload || !documentCategory) {
      showNotification('Por favor seleccione un archivo y categoría', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('archivo', fileToUpload);
    formData.append('categoria', documentCategory);
    formData.append('descripcion', documentDescription);
    
    try {
      setDocumentosLoading(true);
      await pacienteService.uploadDocumento(id, formData);
      showNotification('Documento subido correctamente', 'success');
      
      // Recargar documentos
      const response = await pacienteService.getDocumentos(id);
      setDocumentos(response.data || []);
      
      handleCloseUploadDialog();
    } catch (error) {
      console.error('Error uploading document:', error);
      showNotification('Error al subir el documento: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setDocumentosLoading(false);
    }
  };

  const handleDownloadDocument = async (documentoId, nombreArchivo) => {
    try {
      const response = await pacienteService.downloadDocumento(id, documentoId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      showNotification('Error al descargar el documento', 'error');
    }
  };

  const handleDeleteDocument = async (documentoId) => {
    if (window.confirm('¿Está seguro de eliminar este documento?')) {
      try {
        await pacienteService.deleteDocumento(id, documentoId);
        showNotification('Documento eliminado correctamente', 'success');
        
        // Recargar documentos
        const response = await pacienteService.getDocumentos(id);
        setDocumentos(response.data || []);
      } catch (error) {
        console.error('Error deleting document:', error);
        showNotification('Error al eliminar el documento', 'error');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Paciente no encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title={`${patient.nombres} ${patient.apellidos}`}
        subtitle={`Historia Clínica: ${patient.numero_historia}`}
        icon={
          <Avatar
            alt={`${patient.nombres} ${patient.apellidos}`}
            src={patient.foto ? process.env.REACT_APP_API_URL + patient.foto : '/assets/images/default-user.png'}
            sx={{ 
              width: 64, 
              height: 64, 
              border: '3px solid #fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        }
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Pacientes', to: '/pacientes' },
          { text: `${patient.nombres} ${patient.apellidos}` }
        ]}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Editar - Solo Admin y Admisión */}
            {canEdit() && (
              <Button
                component={RouterLink}
                to={`/pacientes/editar/${patient.id}`}
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
              >
                Editar
              </Button>
            )}
            
            {/* Nueva Cita - Solo Admin y Admisión */}
            {canCreate() && (
              <Button
                component={RouterLink}
                to={`/citas/nueva?paciente=${patient.id}`}
                variant="contained"
                color="primary"
                startIcon={<EventIcon />}
              >
                Nueva Cita
              </Button>
            )}
          </Box>
        }
      />
      
      {/* Alerta de inasistencias */}
      {patient.contador_inasistencias > 2 && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon fontSize="large" />}
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            borderLeft: '4px solid',
            borderColor: 'warning.main',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Paciente con {patient.contador_inasistencias} inasistencias
              </Typography>
              <Typography variant="body2">
                Última inasistencia: {patient.fecha_ultima_inasistencia ? formatDate(patient.fecha_ultima_inasistencia) : 'No registrada'}
              </Typography>
              {patient.observaciones_asistencia && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Observaciones: {patient.observaciones_asistencia}
                </Typography>
              )}
            </Box>
            {canEdit() && (
              <Button 
                variant="outlined" 
                color="warning"
                size="small"
                onClick={() => {/* Función para gestionar inasistencias */}}
                sx={{ ml: 2 }}
              >
                Gestionar
              </Button>
            )}
          </Box>
        </Alert>
      )}
      
      {/* Información básica y foto */}
      <Card elevation={3} sx={{ 
        borderRadius: 2, 
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        mb: 3,
        '&:hover': {
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        }
      }}>
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar
            alt={`${patient.nombres} ${patient.apellidos}`}
            src={patient.foto ? process.env.REACT_APP_API_URL + patient.foto : '/assets/images/default-user.png'}
            sx={{ width: 56, height: 56, border: '2px solid white' }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {patient.nombres} {patient.apellidos}
            </Typography>
            <Typography variant="body2">
              {patient.dni ? `DNI: ${patient.dni}` : 'Sin DNI registrado'}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Chip
              label={patient.estado}
              color={getEstadoColor(patient.estado)}
              sx={{ fontWeight: 'bold' }}
            />
            {canChangeStatus() && (
              <IconButton 
                size="small" 
                color="inherit" 
                onClick={handleOpenCambioEstadoDialog}
                sx={{ ml: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}>
                <ContactsIcon sx={{ mr: 1 }} /> Información Personal
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CakeIcon fontSize="small" sx={{ mr: 1, color: 'primary.light' }} />
                    Fecha de Nacimiento
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.fecha_nacimiento ? `${formatDate(patient.fecha_nacimiento)} (${patient.edad} años)` : 'No registrada'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'primary.light' }} />
                    Fecha Registro
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(patient.fecha_registro)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Sexo
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.sexo === 'M' ? 'Masculino' : patient.sexo === 'F' ? 'Femenino' : patient.sexo || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <HomeIcon fontSize="small" sx={{ mr: 1, color: 'primary.light' }} />
                    Dirección
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.direccion || 'No registrada'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}>
                <PhoneIcon sx={{ mr: 1 }} /> Información de Contacto
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.telefono || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    WhatsApp
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.whatsapp || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.light' }} />
                    Correo Electrónico
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.email || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contacto de Emergencia
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {patient.contacto_emergencia || 'No registrado'}{' '}
                    {patient.telefono_emergencia ? `(${patient.telefono_emergencia})` : ''}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Apoderados */}
      {apoderados.length > 0 && (
        <Card elevation={3} sx={{ 
          mb: 3, 
          borderRadius: 2,
          overflow: 'hidden' 
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'secondary.main', 
            color: 'white'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apoderados ({apoderados.length})
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {apoderados.map((apoderado, index) => (
                <Grid item xs={12} md={6} key={apoderado.id}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.08)',
                      height: '100%',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: 'secondary.light', 
                        color: 'secondary.contrastText',
                        mr: 2
                      }}>
                        {apoderado.nombres.charAt(0)}{apoderado.apellidos.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {apoderado.nombres} {apoderado.apellidos}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {apoderado.parentesco || 'Apoderado'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">DNI</Typography>
                        <Typography variant="body1" fontWeight="medium">{apoderado.dni || 'No registrado'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Teléfono</Typography>
                        <Typography variant="body1" fontWeight="medium">{apoderado.telefono || 'No registrado'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Email</Typography>
                        <Typography variant="body1" fontWeight="medium">{apoderado.email || 'No registrado'}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
      
      {/* Pestañas con información detallada */}
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="patient tabs"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              '& .MuiTab-root': {
                minHeight: 64,
                fontWeight: 'medium',
              },
              '& .Mui-selected': {
                fontWeight: 'bold',
              }
            }}
          >
            <Tab 
              icon={<InfoIcon />} 
              iconPosition="start" 
              label="Información Detallada" 
            />
            <Tab 
              icon={<EventIcon />} 
              iconPosition="start" 
              label="Citas" 
            />
            <Tab 
              icon={<DescriptionIcon />} 
              iconPosition="start" 
              label="Documentos" 
            />
            <Tab 
              icon={<HistoryIcon />} 
              iconPosition="start" 
              label="Historial Estados" 
            />
          </Tabs>
        </Box>
        
        {/* Tab: Información Detallada */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}>
                <SchoolIcon sx={{ mr: 1 }} /> Información Académica y Laboral
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Grado Académico
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.grado_academico || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Escolaridad
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.escolaridad || 'No registrada'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Institución Educativa
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.institucion_educativa || 'No registrada'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Trabajo Actual
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.trabajo_actual || 'No registrado'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}>
                <LocalHospitalIcon sx={{ mr: 1 }} /> Información Médica
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Grupo Sanguíneo
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.grupo_sanguineo || 'No registrado'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Lateralidad
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.lateralidad || 'No registrada'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Alergias
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.alergias || 'No registradas'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Condiciones Preexistentes
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.condiciones_preexistentes || 'No registradas'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Medicación Actual
                  </Typography>
                  <Typography variant="body1" gutterBottom fontWeight="medium">
                    {patient.medicacion_actual || 'No registrada'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            
            {patient.notas_administrativas && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  pb: 1,
                  mb: 2
                }}>
                  <InfoIcon sx={{ mr: 1 }} /> Notas Administrativas
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderColor: 'info.main' }}>
                  <Typography variant="body1">
                    {patient.notas_administrativas}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        
        {/* Tab: Citas */}
        <TabPanel value={tabValue} index={1}>
          {citasLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : citas.length === 0 ? (
            <Box textAlign="center" my={5}>
              <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No hay citas registradas para este paciente
              </Typography>
              {canCreate() && (
                <Button
                  component={RouterLink}
                  to={`/citas/nueva?paciente=${patient.id}`}
                  variant="contained"
                  color="primary"
                  startIcon={<EventIcon />}
                  sx={{ mt: 2, borderRadius: 8, px: 3 }}
                >
                  Agendar Cita
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom color="primary" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}>
                <EventIcon sx={{ mr: 1 }} /> Historial de Citas
              </Typography>
              
              <Grid container spacing={2}>
                {citas.map((cita) => (
                  <Grid item xs={12} sm={6} md={4} key={cita.id}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 
                          cita.estado === 'Programada' ? 'primary.light' : 
                          cita.estado === 'Confirmada' ? 'success.light' : 
                          cita.estado === 'Cancelada' ? 'error.light' : 
                          cita.estado === 'Realizada' ? 'info.light' : 
                          cita.estado === 'No asistió' ? 'warning.light' : 
                          'grey.300',
                        color: 'white'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatDate(cita.fecha)}
                          </Typography>
                          <Chip
                            label={cita.estado}
                            size="small"
                            color={
                              cita.estado === 'Programada' ? 'primary' :
                              cita.estado === 'Confirmada' ? 'success' :
                              cita.estado === 'Cancelada' ? 'error' :
                              cita.estado === 'Realizada' ? 'default' :
                              cita.estado === 'No asistió' ? 'warning' :
                              'info'
                            }
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Typography variant="body2">
                          {format(parseISO(`2000-01-01T${cita.hora_inicio}`), 'h:mm a', { locale: es })} - 
                          {format(parseISO(`2000-01-01T${cita.hora_fin}`), 'h:mm a', { locale: es })}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Especialista
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {cita.especialista?.usuario?.nombre || 'No especificado'}
                        </Typography>
                        
                        <Typography variant="subtitle2" color="textSecondary">
                          Especialidad
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {cita.especialidad?.nombre || 'No especificada'}
                        </Typography>
                        
                        {cita.motivo && (
                          <>
                            <Typography variant="subtitle2" color="textSecondary">
                              Motivo
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {cita.motivo}
                            </Typography>
                          </>
                        )}
                        
                        <Button
                          component={RouterLink}
                          to={`/citas/${cita.id}`}
                          color="primary"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{ mt: 2 }}
                          fullWidth
                          variant="outlined"
                        >
                          Ver Detalles
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </TabPanel>
        
        {/* Tab: Documentos */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
            }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} /> Documentos del Paciente
              </Typography>
              {canUploadDocuments() && (
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleOpenUploadDialog}
                  color="primary"
                  sx={{ borderRadius: 2 }}
                >
                  Subir Documento
                </Button>
              )}
            </Box>
            
            {documentosLoading ? (
              <Box display="flex" justifyContent="center" my={5}>
                <CircularProgress />
              </Box>
            ) : documentos.length === 0 ? (
              <Paper 
                elevation={0}
                sx={{ 
                  textAlign: 'center', 
                  py: 6, 
                  px: 3,
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  border: '2px dashed rgba(0,0,0,0.1)'
                }}
              >
                <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No hay documentos registrados
                </Typography>
                {canUploadDocuments() && (
                  <>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Sube el primer documento para este paciente
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<CloudUploadIcon />}
                      onClick={handleOpenUploadDialog}
                      color="primary"
                      sx={{ mt: 1, borderRadius: 8, px: 3 }}
                    >
                      Subir Documento
                    </Button>
                  </>
                )}
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {documentos.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 0, 
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'primary.light', 
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}>
                        <InsertDriveFileIcon />
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 'bold',
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {doc.nombre || doc.nombre_documento || doc.nombre_archivo || 'Documento'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Categoría: {doc.categoria || 'Sin categoría'}
                        </Typography>
                        
                        {doc.descripcion && (
                          <Typography variant="body2" color="textSecondary" sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mb: 1
                          }}>
                            {doc.descripcion}
                          </Typography>
                        )}
                        
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                          Subido el {formatDate(doc.fecha_subida || doc.created_at)}
                        </Typography>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadDocument(doc.id, doc.nombre || doc.nombre_documento || doc.nombre_archivo || 'documento')}
                            variant="outlined"
                          >
                            Descargar
                          </Button>
                          {canDeleteDocuments() && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </TabPanel>
        
        {/* Tab: Historial Estados */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 1,
              mb: 2
            }}>
              <HistoryIcon sx={{ mr: 1 }} /> Historial de Cambios de Estado
            </Typography>
            <HistorialCambiosEstado pacienteId={id} />
          </Box>
        </TabPanel>
      </Card>
      
      {/* Diálogo cambio de estado */}
      {canChangeStatus() && (
        <CambioEstadoPaciente
          open={cambioEstadoDialogOpen}
          onClose={handleCloseCambioEstadoDialog}
          paciente={patient}
          onEstadoChanged={handleEstadoChanged}
        />
      )}
      
      {/* Diálogo subir documento */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          fontWeight: 'bold'
        }}>
          Subir Documento
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2 }}>
            <input
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="raised-button-file">
              <Button 
                variant="outlined" 
                component="span" 
                fullWidth 
                sx={{ 
                  height: 100, 
                  borderStyle: 'dashed', 
                  borderWidth: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 32 }} />
                <Typography>Seleccionar Archivo</Typography>
              </Button>
            </label>
            
            {fileToUpload && (
              <Paper sx={{ 
                p: 2, 
                mt: 2, 
                display: 'flex', 
                alignItems: 'center',
                borderRadius: 2,
                backgroundColor: 'success.light',
                color: 'success.contrastText'
              }}>
                <InsertDriveFileIcon sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 'medium' }}>
                  {fileToUpload.name}
                </Typography>
                <Typography variant="caption">
                  {(fileToUpload.size / 1024).toFixed(1)} KB
                </Typography>
              </Paper>
            )}
            
            <FormControl fullWidth sx={{ mt: 3, mb: 2 }}>
              <InputLabel id="category-label">Categoría</InputLabel>
              <Select
                labelId="category-label"
                value={documentCategory}
                label="Categoría"
                onChange={(e) => setDocumentCategory(e.target.value)}
              >
                <MenuItem value="Historia Clínica">Historia Clínica</MenuItem>
                <MenuItem value="Informe Médico">Informe Médico</MenuItem>
                <MenuItem value="Exámenes">Exámenes</MenuItem>
                <MenuItem value="Consentimiento">Consentimiento</MenuItem>
                <MenuItem value="Documentación Personal">Documentación Personal</MenuItem>
                <MenuItem value="Otros">Otros</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Descripción (opcional)"
              value={documentDescription}
              onChange={(e) => setDocumentDescription(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseUploadDialog} variant="text">
            Cancelar
          </Button>
          <Button 
            onClick={handleUploadDocument} 
            variant="contained"
            disabled={!fileToUpload || !documentCategory}
            startIcon={<CloudUploadIcon />}
          >
            Subir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PacienteDetail;